import { Controller } from "@hotwired/stimulus"
import { Modal } from "bootstrap"
import defaultCalendarOptions, {
  calendarDateForWeekday,
  calendarEventTimeFormat,
  calendarTimeZone,
  calendarWeekday,
  calendarWeekdayHeaderContent,
  calendarZonedDateParts,
  currentScrollTime,
  renderCalendarCentered
} from "@appComponents/helpers/full-calendar-default-config"
import FlashMessage from "src/shared/components/flash-messages"
import { loadFullCalendar } from "@appComponents/helpers/load_full_calendar"

const WEEKLY_AVAILABILITIES_ENDPOINT = "/api/v1/weekly_availabilities"
const DEFAULT_EVENT_COLOR = "#3788d8"

// Set once FullCalendar and Moment are loaded (connect).
let Calendar, timeZonePlugin, timeGridPlugin, interactionPlugin, frLocale, moment

export default class extends Controller {
  static targets = [
    "calendar",
    "applyPlanningButton",
    "availabilityModalTitle",
    "availabilityErrors",
    "wday",
    "startTime",
    "endTime",
    "appointmentsNumber",
    "practitionerOfficeId",
    "deleteAvailabilityButton",
    "submitAvailabilityButton"
  ]

  static values = {
    practitionerId: Number,
    appointmentDuration: Number,
    availabilityModalId: String,
    availabilityEventTitle: String,
    newAvailabilityTitle: String,
    editAvailabilityTitle: String,
    createActionLabel: String,
    updateActionLabel: String,
    createdFlashMessage: String,
    updatedFlashMessage: String,
    deletedFlashMessage: String
  }

  async connect () {
    this.weeklyAvailabilities = []
    this.currentAvailability = null
    this.editing = false

    const connection = (this.connection = Symbol("connection"))
    const calendar = await loadFullCalendar()
    if (this.connection !== connection) return // disconnected (or reconnected) while loading
    ;({ Calendar, timeZonePlugin, timeGridPlugin, interactionPlugin, frLocale, moment } = calendar)

    this.initializeCalendar()
  }

  disconnect () {
    this.connection = null
    this.calendar?.destroy()
  }

  initializeCalendar () {
    this.calendar = new Calendar(this.calendarTarget, {
      ...defaultCalendarOptions,
      plugins: [timeZonePlugin, timeGridPlugin, interactionPlugin],
      locale: frLocale,
      timeZone: calendarTimeZone(),
      eventTimeFormat: calendarEventTimeFormat,
      headerToolbar: false,
      initialView: "timeGridWeek",
      firstDay: 1,
      dayHeaderContent: calendarWeekdayHeaderContent,
      nowIndicator: false,
      scrollTime: currentScrollTime(),
      selectable: true,
      select: (info) => this.openNewAvailability(info),
      eventClick: (info) => this.openExistingAvailability(info),
      events: (fetchInfo, successCallback, failureCallback) => {
        this.fetchWeeklyAvailabilities()
          .then((events) => successCallback(events))
          .catch((error) => failureCallback(error))
      }
    })

    renderCalendarCentered(this.calendar, this.calendarTarget)
  }

  async fetchWeeklyAvailabilities () {
    const response = await fetch(`${WEEKLY_AVAILABILITIES_ENDPOINT}?${new URLSearchParams({
      practitioner_id: this.practitionerIdValue
    })}`, {
      headers: { Accept: "application/json" },
      credentials: "same-origin"
    })

    const data = await response.json()
    this.weeklyAvailabilities = data.data || []

    return this.buildEvents()
  }

  buildEvents () {
    return this.weeklyAvailabilities.map((weeklyAvailability) => {
      const attributes = weeklyAvailability.attributes
      const day = attributes.wday
      const date = calendarDateForWeekday(day)
      const color = attributes.color || DEFAULT_EVENT_COLOR

      return {
        id: weeklyAvailability.id,
        title: attributes.title || this.availabilityEventTitleValue,
        start: `${date}T${attributes.start_time.slice(0, 5)}:00`,
        end: `${date}T${attributes.end_time.slice(0, 5)}:00`,
        extendedProps: { weeklyAvailability },
        backgroundColor: "transparent",
        borderColor: color,
        classNames: ["fc-event--availability"]
      }
    })
  }

  openNewAvailability (selectionInfo) {
    this.editing = false
    this.currentAvailability = this.newAvailabilityPayload()

    const start = calendarZonedDateParts(selectionInfo.startStr || selectionInfo.start)
    const end = calendarZonedDateParts(selectionInfo.endStr || selectionInfo.end)

    this.currentAvailability.attributes.wday = calendarWeekday(selectionInfo.startStr || selectionInfo.start)
    this.currentAvailability.attributes.start_time = start.time
    this.currentAvailability.attributes.end_time = end.time

    if (!this.currentAvailability.attributes.practitioner_office_id && this.practitionerOfficeIdTarget.options.length) {
      this.currentAvailability.attributes.practitioner_office_id = this.practitionerOfficeIdTarget.value
    }

    this.calendar.unselect()
    this.showAvailabilityModalWithForm()
  }

  openExistingAvailability (eventInfo) {
    const weeklyAvailability = eventInfo.event.extendedProps.weeklyAvailability
    if (!weeklyAvailability) return

    this.editing = true
    this.currentAvailability = structuredClone(weeklyAvailability)
    this.showAvailabilityModalWithForm()
  }

  showAvailabilityModalWithForm () {
    const modalElement = document.getElementById(this.availabilityModalIdValue)
    if (!modalElement) return

    modalElement.addEventListener("shown.bs.modal", () => {
      this.populateAvailabilityForm()
    }, { once: true })

    Modal.getOrCreateInstance(modalElement).show()
    this.populateAvailabilityForm()
  }

  populateAvailabilityForm () {
    const attributes = this.currentAvailability.attributes

    this.clearErrors(this.availabilityErrorsTarget)
    this.wdayTarget.value = attributes.wday ?? ""
    this.startTimeTarget.value = attributes.start_time || ""
    this.endTimeTarget.value = attributes.end_time || ""
    this.appointmentsNumberTarget.value = attributes.appointments_number || 1

    if (attributes.practitioner_office_id) {
      this.practitionerOfficeIdTarget.value = attributes.practitioner_office_id
    } else if (this.practitionerOfficeIdTarget.options.length) {
      this.practitionerOfficeIdTarget.selectedIndex = 0
    }

    this.updateAvailabilityTitle()
    this.updateAvailabilityActions()
    this.updateEndTimeFromDuration()
  }

  updateEndTimeFromDuration () {
    const start = moment(this.startTimeTarget.value, "HH:mm")
    if (!start.isValid()) return

    const appointmentsNumber = parseInt(this.appointmentsNumberTarget.value, 10) || 1
    const duration = this.appointmentDurationValue * appointmentsNumber

    this.endTimeTarget.value = start.add(duration, "minutes").format("HH:mm")
  }

  updateAvailabilityTitle () {
    this.availabilityModalTitleTarget.textContent = this.editing
      ? this.editAvailabilityTitleValue
      : this.newAvailabilityTitleValue
  }

  updateAvailabilityActions () {
    this.submitAvailabilityButtonTarget.textContent = this.editing
      ? this.updateActionLabelValue
      : this.createActionLabelValue
    this.deleteAvailabilityButtonTarget.classList.toggle("d-none", !this.editing)
  }

  enableApplyPlanningButton () {
    this.applyPlanningButtonTarget.disabled = false
  }

  async submitAvailability () {
    this.syncAvailabilityAttributes()

    const method = this.editing ? "PUT" : "POST"
    const url = this.editing
      ? `${WEEKLY_AVAILABILITIES_ENDPOINT}/${this.currentAvailability.id}`
      : WEEKLY_AVAILABILITIES_ENDPOINT

    const response = await fetch(url, {
      method,
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        "X-CSRF-Token": this.csrfToken
      },
      credentials: "same-origin",
      body: JSON.stringify({ weekly_availability: this.currentAvailability.attributes })
    })

    if (response.ok) {
      this.hideModal(this.availabilityModalIdValue)
      this.calendar.refetchEvents()
      this.showFlash(
        this.editing ? this.updatedFlashMessageValue : this.createdFlashMessageValue,
        "success"
      )
      this.enableApplyPlanningButton()
      return
    }

    const data = await response.json()
    this.currentAvailability = data.data
    this.displayErrors(this.availabilityErrorsTarget, this.currentAvailability.attributes.errors || [])
  }

  async deleteAvailability () {
    const response = await fetch(`${WEEKLY_AVAILABILITIES_ENDPOINT}/${this.currentAvailability.id}`, {
      method: "DELETE",
      headers: {
        Accept: "application/json",
        "X-CSRF-Token": this.csrfToken
      },
      credentials: "same-origin"
    })

    if (response.ok) {
      this.hideModal(this.availabilityModalIdValue)
      this.calendar.refetchEvents()
      this.showFlash(this.deletedFlashMessageValue, "success")
      this.enableApplyPlanningButton()
      return
    }

    const data = await response.json()
    this.currentAvailability = data.data
    this.displayErrors(this.availabilityErrorsTarget, this.currentAvailability.attributes.errors || [])
  }

  syncAvailabilityAttributes () {
    this.currentAvailability.attributes.wday = parseInt(this.wdayTarget.value, 10)
    this.currentAvailability.attributes.start_time = this.startTimeTarget.value
    this.currentAvailability.attributes.end_time = this.endTimeTarget.value
    this.currentAvailability.attributes.appointments_number = this.appointmentsNumberTarget.value
    this.currentAvailability.attributes.practitioner_office_id = this.practitionerOfficeIdTarget.value
  }

  newAvailabilityPayload () {
    return {
      id: null,
      attributes: {
        wday: "",
        start_time: "",
        end_time: "",
        practitioner_office_id: "",
        appointments_number: 1,
        errors: []
      }
    }
  }

  hideModal (modalId) {
    const element = document.getElementById(modalId)
    const modal = element ? Modal.getInstance(element) : null
    if (modal) modal.hide()
  }

  showFlash (message, kind) {
    new FlashMessage(message, kind)
  }

  clearErrors (target) {
    target.innerHTML = ""
  }

  displayErrors (target, errors) {
    if (!errors?.length) {
      this.clearErrors(target)
      return
    }

    target.innerHTML = `
      <div class="alert alert-danger">
        <ul class="mb-0">
          ${errors.map((error) => `<li>${error}</li>`).join("")}
        </ul>
      </div>
    `
  }

  get csrfToken () {
    return document.querySelector("meta[name='csrf-token']")?.content
  }
}
